import React, { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import Modal from '../modal/Modal'
import styles from './new-requests.module.scss'
import dayjs from 'dayjs'
import isToday from 'dayjs/plugin/isToday'
import NewRequestButtons from './NewRequestButtons'
import ServiceRequestDetails from './ServiceRequestDetails'
import ServiceRequestModifyForm from './ServiceRequestModify'
import { STATUSES } from './constants'

export default function ServiceRequestsLineItem ({
  request,
  index,
  refetchServiceRequests,
  onOpenConfirm
}) {
  const utc = require('dayjs/plugin/utc')
  dayjs.extend(isToday)
  dayjs.extend(utc)

  const [declineForm, setDeclineForm] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)

  const service = request[0]
  // OPEN, ACCEPTED, PENDING_RESOLUTION, DECLINED
  const open = service.status === 'OPEN'
  const pending = service.status === 'PENDING_RESOLUTION'
  const confirmed = service.status === 'ACCEPTED'
  const [serviceDate] = service.serviceDate.split('T')
  const isRequestToday = dayjs(serviceDate).isToday()
  const isRequestBeforeToday = dayjs(serviceDate).isBefore(new Date(), 'day')
  const requestedTime =
  service.requestedStartTime && service.requestedEndTime
    ? `${dayjs.utc(service.requestedStartTime).format('h:mm a')} - ${dayjs.utc(service.requestedEndTime).format('h:mm a')}`
    : ''
  const customerName = [service.firstName, service.lastName].filter(Boolean).join(' ')

  function formatAssetList () {
    const svcs = request.reduce((acc, service) => {
      const resource = service.resourceType.name
      acc[resource] = (acc[resource] || 0) + 1
      return acc
    }, {})

    const formattedProducts = Object.keys(svcs).map((size, i) => {
      const product = svcs[size] > 1 ? `(${svcs[size]})` : ''
      return ` ${size}${product}${(Object.keys(svcs).length > 1 && !Object.keys(svcs).length === i + 1) ? ',' : ''}`
    })
    return formattedProducts.join(',')
  }

  function formatTitle () {
    if (!open) {
      return `${service.ticketType.name} - ${STATUSES[service.status]}`
    }

    if (request.length === 1) {
      return `${service.ticketType.name} - ${service.resourceType.name} (${service.ticketType.shortCode}-${service.resourceType.shortCode})`
    }

    return `${service.ticketType.name} - ${formatAssetList()}`
  }

  function toggleDeclineForm (val) {
    if (typeof val === 'boolean') {
      setDeclineForm(val)
    } else {
      setDeclineForm(!declineForm)
    }
  }

  function toggleDetails (val) {
    if (typeof val === 'boolean') {
      setDetailsOpen(val)
    } else {
      setDetailsOpen(!detailsOpen)
    }
  }

  function handleConfirm () {
    setDeclineForm(false)
    onOpenConfirm(request)
  }

  function detailsIcon () {
    return {
      ORDER_CONFIRMED: 'check_circle',
      PENDING_RESOLUTION: 'pending',
      OPEN: 'info'
    }
  }

  const buttonView = () => {
    return (
      <NewRequestButtons
        index={index}
        handleConfirm={handleConfirm}
        toggleDeclineForm={toggleDeclineForm}
        request={{ tickets: request }}
        toggleDetails={toggleDetails}
        isRequestBeforeToday={isRequestBeforeToday}
        declineText='Modify/Decline'
        confirmText={request.length === 1 ? 'Create Ticket' : 'Create Tickets'}
        className='ml-20'
      />
    )
  }

  return (
    <>
      <Modal isOpen={declineForm} closeModal={() => toggleDeclineForm(false)} className={styles['fix-request-modal']}>
        <ServiceRequestModifyForm
          request={request}
          toggleDeclineForm={toggleDeclineForm}
          handleConfirm={handleConfirm}
          canConfirm={!isRequestBeforeToday}
          refetchServiceRequests={refetchServiceRequests}
        />
      </Modal>
      <div className='bg-white p-4 pr-0 border-b border-gray-light'>
        <div className='flex flex-row w-full gap-2'>
          <div>
            <button
              type='button'
              onClick={() => toggleDetails(true)}>
              <i
                className={classNames('material-icons font-size-1-5em relative cursor-pointer', {
                  'text-green': confirmed,
                  'text-gray': pending
                })}>
                {detailsIcon()[service.status]}
              </i>
            </button>
          </div>
          <div className='flex flex-row w-full justify-between'>
            <div className={styles.title}>
              <strong>{formatTitle()}</strong>
            </div>
            {(open || pending) && (
              <div className={classNames('text-2xl shrink-0 ml-2', { 'text-orange font-medium': isRequestToday })}>
                {isRequestToday ? 'Today' : dayjs(serviceDate).format('M/D/YYYY')}
              </div>
            )}
          </div>
        </div>

        {open && requestedTime && (
          <div className={classNames('ml-14 font-size-1em', { 'text-orange': isRequestToday })}>
            {`Requested: ${request.length > 1 ? '(See details)' : requestedTime}`}
          </div>
        )}

        <div className='-mx-4 flex flex-col gap-1'>
          {customerName && (
            <div className='w-full flex flex-row pl-20'>
              <span className='text-black leading-tight'>{customerName}</span>
            </div>
          )}
          <div className={classNames('inline-block leading-tight text-black ml-20', styles.address)}>
            {service.addressline1}
            <br />
            {service.city || 'unknown'}, {service.state || 'unknown'} {service.zip || 'unknown'}
            {service.addressDetails && ` (${service.addressDetails})`}
          </div>
          {open && buttonView()}
        </div>

        {detailsOpen && <ServiceRequestDetails
          request={request}
          toggleDetails={toggleDetails}
          buttonView={buttonView}
                        />}
      </div>
    </>
  )
}

ServiceRequestsLineItem.propTypes = {
  request: PropTypes.arrayOf(PropTypes.any).isRequired,
  index: PropTypes.number.isRequired,
  refetchServiceRequests: PropTypes.func.isRequired,
  onOpenConfirm: PropTypes.func.isRequired
}
